import { useEffect, useMemo, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CountryCode } from 'libphonenumber-js'
import './style.scss'
import utility from 'src/utils/utility'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import {
    Header, BackLink, Card, Button, TextField, PhoneField, OptionToggle,
    InfoBanner, FileUpload, CircularProgress, Text, Flex, Box, tokens,
} from 'src/componentv2'
import type { AliasMode, LogoWithUrl } from 'src/model/v2/interface/props'
import type { InstitutionProfileRequest } from 'src/model/v2/interface/params'
import {
    REQUIRED_MSG, PAGE_PADDING_X, fontRegular, fontMedium, fontBold,
    LOGO_ACCEPT, LOGO_HINT,
} from 'src/model/v2/interface/constants'
import {
    makeFieldError, makePhoneFieldError, isFormValid as checkFormValid,
    handleLogoSelect, handleLogoRemove,
} from 'src/app/dashboardv2/function'
import { useConfigurationChecklist } from 'src/app/dashboardv2/hooks/useConfigurationChecklist'
import { useSidebarOverride } from 'src/app/dashboardv2/layout/DashboardLayout'

const MAX_PROGRESS = 25


interface FormState {
    notificationEmail: string
    contactEmail: string
    country: CountryCode
    phone: string
    businessAddress: string
    websiteUrl: string
    privacyUrl: string
    termsUrl: string
    appName: string
    testAccountUsername: string
    testAccountNumber: string
    testAccountCustomerId: string
    loanPolicyUrl: string
    recovaInstitutionCode: string
    androidAppUrl: string
    iosAppUrl: string
}

const InstitutionProfile = () => {
    const navigate = useNavigate()
    const {
        institutionProfile, getInstitutionProfile, updateInstitutionProfile,
        updateAppDownloadLinks, getOnboardingChecklist,
    } = useContext(ConfigurationApiContext)

    const { loading: checklistLoading, isComplete } = useConfigurationChecklist()
    useSidebarOverride(checklistLoading ? null : { visible: isComplete })

    const [form, setForm] = useState<FormState>({
        notificationEmail: '', contactEmail: '',
        country: 'NG', phone: '',
        businessAddress: '', websiteUrl: '', privacyUrl: '', termsUrl: '',
        appName: '',
        testAccountUsername: '', testAccountNumber: '', testAccountCustomerId: '',
        loanPolicyUrl: '', recovaInstitutionCode: '',
        androidAppUrl: '', iosAppUrl: '',
    })

    const [aliasMode, setAliasMode] = useState<AliasMode>('none')
    const [alias, setAlias] = useState('')
    const [customizedUrl, setCustomizedUrl] = useState('')

    const [primaryLogo, setPrimaryLogo] = useState<LogoWithUrl | null>(null)
    const [favicon, setFavicon] = useState<LogoWithUrl | null>(null)
    const [primaryLogoError, setPrimaryLogoError] = useState<string | undefined>()
    const [faviconError, setFaviconError] = useState<string | undefined>()
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [loadingProfile, setLoadingProfile] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [phoneValid, setPhoneValid] = useState(true)
    const [profileCompleted, setProfileCompleted] = useState(false)

    const hydrateFormFrom = (data: any) => {
        setForm((f) => ({
            ...f,
            notificationEmail: data.institutionEmail || '',
            contactEmail: data.correspondenceEmail || '',
            phone: data.correspondencePhone || '',
            businessAddress: data.address || '',
            websiteUrl: data.website || '',
            privacyUrl: data.privacyPolicyUrl || '',
            termsUrl: data.termsAndConditionsUrl || '',
            appName: data.appName || '',
            testAccountUsername: data.testAccountUsername || '',
            testAccountNumber: data.testAccountNumber || '',
            testAccountCustomerId: data.testAccountCustomerID || '',
            loanPolicyUrl: data.loanPolicyUrl || '',
            recovaInstitutionCode: data.recovaInstitutionCode || '',
            androidAppUrl: data.androidAppUrl || '',
            iosAppUrl: data.iosAppUrl || '',
        }))

        const hasAlias = !!data.alias
        const hasUrl = !!data.customizedUrl

        setAlias(data.alias || '')
        setCustomizedUrl(data.customizedUrl || '')
        setAliasMode(
            hasAlias && hasUrl ? 'both' :
                hasAlias ? 'alias' :
                    hasUrl ? 'url' :
                        'none'
        )

        if (data.logo) setPrimaryLogo({ name: 'Primary Logo', size: '', url: data.logo })
        if (data.favIcon) setFavicon({ name: 'Favicon', size: '', url: data.favIcon })
    }

    useEffect(() => {
        (async () => {
            if (institutionProfile) {
                hydrateFormFrom(institutionProfile)
            } else {
                const data = await getInstitutionProfile()
                if (data) hydrateFormFrom(data)
            }
            setLoadingProfile(false)

            const checklist = await getOnboardingChecklist()
            if (Array.isArray(checklist)) {
                const step = checklist.find((c: any) => c.key === 'InstitutionProfile')
                if (step?.value?.status === 'Completed') setProfileCompleted(true)
            }
        })()
    }, [])

    const progress = useMemo(() => {
        const fields = [
            form.notificationEmail, form.contactEmail, form.phone, form.businessAddress,
            form.websiteUrl, form.privacyUrl, form.termsUrl, form.appName,
            primaryLogo, favicon,
        ]
        const completed = fields.filter(Boolean).length
        return Math.round((completed / fields.length) * MAX_PROGRESS)
    }, [form, primaryLogo, favicon])

    const errors = useMemo(() => ({
        notificationEmail: !form.notificationEmail,
        contactEmail: !form.contactEmail,
        phone: !form.phone || !phoneValid,
        businessAddress: !form.businessAddress,
        websiteUrl: !form.websiteUrl,
        privacyUrl: !form.privacyUrl,
        termsUrl: !form.termsUrl,
        appName: !form.appName,
        primaryLogo: !primaryLogo || !!primaryLogoError,
        favicon: !favicon || !!faviconError,
    }), [form, phoneValid, primaryLogo, favicon, primaryLogoError, faviconError])

    const isFormValid = useMemo(() => checkFormValid(errors), [errors])

    const set = <K extends keyof FormState>(key: K) => (value: FormState[K]) =>
        setForm((f) => ({ ...f, [key]: value }))

    const fieldError = (key: keyof typeof errors) =>
        makeFieldError(submitAttempted, errors[key])

    const phoneFieldError = () => makePhoneFieldError(submitAttempted, form.phone)

    const onSelectPrimaryLogo = handleLogoSelect(setPrimaryLogo, setPrimaryLogoError)
    const onRemovePrimaryLogo = handleLogoRemove(setPrimaryLogo, setPrimaryLogoError)
    const onSelectFavicon = handleLogoSelect(setFavicon, setFaviconError)
    const onRemoveFavicon = handleLogoRemove(setFavicon, setFaviconError)

    const handleSubmit = async () => {
        if (isSubmitting) return

        if (!isFormValid) {
            setSubmitAttempted(true)
            return
        }

        const hasPrimaryLogoFile = primaryLogo?.file !== undefined
        const hasFaviconFile = favicon?.file !== undefined

        if (!hasPrimaryLogoFile && !primaryLogo?.url) {
            setSubmitAttempted(true)
            return
        }
        if (!hasFaviconFile && !favicon?.url) {
            setSubmitAttempted(true)
            return
        }

        const payload: Partial<InstitutionProfileRequest> = {
            NotificationEmail: form.notificationEmail,
            ContactEmail: form.contactEmail,
            ContactPhoneNumber: form.phone,
            BusinessAddress: form.businessAddress,
            WebsiteUrl: form.websiteUrl,
            PrivacyPolicyUrl: form.privacyUrl,
            TermsAndConditionUrl: form.termsUrl,
            ApplicationName: form.appName,
            DomainType: 'custom',
            Domain: form.websiteUrl,
        }

        if (primaryLogo?.file) payload.PrimaryLogo = primaryLogo.file
        if (favicon?.file) payload.FaviconLogo = favicon.file

        // Advanced fields only exist in configuration mode's form state and
        // are only sent when the person has actually filled them in — no
        // point sending empty strings for optional backend fields.
        if (isComplete) {
            if (form.testAccountUsername) payload.TestAccountUsername = form.testAccountUsername
            if (form.testAccountNumber) payload.TestAccountNumber = form.testAccountNumber
            if (form.testAccountCustomerId) payload.TestAccountCustomerID = form.testAccountCustomerId
            if (form.loanPolicyUrl) payload.LoanPolicyUrl = form.loanPolicyUrl
            if (form.recovaInstitutionCode) payload.RecovaInstitutionCode = form.recovaInstitutionCode
        }

        setIsSubmitting(true)
        try {
            const ok = await updateInstitutionProfile(payload)
            if (!ok) return

            // App download links live on their own dedicated endpoint (not part
            // of the shared Profile payload above), and only exist in
            // configuration mode's Advanced Settings — same gating as loanPolicyUrl.
            if (isComplete) {
                const linksOk = await updateAppDownloadLinks({
                    androidAppUrl: form.androidAppUrl,
                    iosAppUrl: form.iosAppUrl,
                })
                if (!linksOk) return
            }

            // Only auto-advance to the next wizard step while still onboarding.
            // Once setup is complete this is just "Save Changes" — stay put.
            if (!isComplete) {
                navigate('/dashboardv2/institution-branding')
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    if (loadingProfile || checklistLoading) return null

    const formCard = (
        <Card topMargin="2" padding="3.2" radius="1.2">
            <div className="form-row">
                <TextField
                    label="Notification Email" placeholder="Enter notification email"
                    helperText="Receives notifications for app uploads and system alerts"
                    error={fieldError('notificationEmail')}
                    value={form.notificationEmail} onChange={set('notificationEmail')}
                />
                <TextField
                    label="Contact Email" placeholder="Enter contact email"
                    helperText="Public-facing contact email"
                    error={fieldError('contactEmail')}
                    value={form.contactEmail} onChange={set('contactEmail')}
                />
            </div>

            <div className="form-row">
                <PhoneField
                    label="Contact Phone number"
                    country={form.country} onCountryChange={set('country')}
                    error={phoneFieldError()}
                    value={form.phone} onChange={set('phone')}
                    onValidityChange={setPhoneValid}
                />
                <TextField
                    label="Business Address" placeholder="Enter business Address"
                    error={fieldError('businessAddress')}
                    value={form.businessAddress} onChange={set('businessAddress')}
                />
            </div>

            <div className="form-row">
                <TextField
                    label="Website URL" prefix="http://"
                    error={fieldError('websiteUrl')}
                    value={form.websiteUrl} onChange={set('websiteUrl')}
                />
                <TextField
                    label="Privacy Policy URL" prefix="http://"
                    error={fieldError('privacyUrl')}
                    value={form.privacyUrl} onChange={set('privacyUrl')}
                />
            </div>

            <div className="form-row full-width">
                <TextField
                    label="Terms and Conditions URL" prefix="http://"
                    error={fieldError('termsUrl')}
                    value={form.termsUrl} onChange={set('termsUrl')}
                />
            </div>

            <Text topMargin="3.2" fontSize="1.8" style={fontMedium} className="institution-subsection">
                Application Details
            </Text>

            <Box topMargin="1.6">
                <TextField
                    label="App Name" placeholder="Enter App Name"
                    error={fieldError('appName')}
                    value={form.appName} onChange={set('appName')}
                />
            </Box>

            {aliasMode === 'both' && (
                <Box topMargin="1.6">
                    <InfoBanner className="institution-info-banner" variant="warning">
                        Both an alias and a customised URL are set for this institution. This
                        configuration isn't meant to happen — please reach out to support to resolve it.
                    </InfoBanner>
                </Box>
            )}

            {aliasMode !== 'none' && (
                <>
                    <Box topMargin="1.6" style={{ pointerEvents: 'none', opacity: 0.6 }}>
                        <OptionToggle
                            value={aliasMode === 'url' ? 'url' : 'alias'}
                            onChange={() => {}}
                            options={[
                                { label: 'Alias', value: 'alias' },
                                { label: 'Customised URL', value: 'url' },
                            ]}
                        />
                    </Box>

                    <Box topMargin="1.6">
                        <InfoBanner className="institution-info-banner">
                            This is the app name displayed when using the branded domain.
                        </InfoBanner>
                    </Box>

                    {(aliasMode === 'alias' || aliasMode === 'both') && (
                        <Box topMargin="1.6">
                            <TextField
                                label="Display Alias"
                                value={alias}
                                onChange={() => {}}
                                disabled
                                inputProps={{ readOnly: true }}
                            />
                        </Box>
                    )}

                    {(aliasMode === 'url' || aliasMode === 'both') && (
                        <Box topMargin="1.6">
                            <TextField
                                label="Customised URL"
                                value={customizedUrl}
                                onChange={() => {}}
                                disabled
                                inputProps={{ readOnly: true }}
                            />
                        </Box>
                    )}
                </>
            )}

            <Text topMargin="3.2" fontSize="1.8" style={fontMedium} className="institution-subsection">
                Logo
            </Text>

            <div className="form-row">
                <Box>
                    <Text fontSize="1.4" style={{ ...fontMedium, color: '#344054' }}>
                        Primary Logo
                    </Text>
                    <Box topMargin="0.8">
                        <FileUpload
                            className="institution-upload"
                            value={primaryLogo}
                            hint={LOGO_HINT}
                            accept={LOGO_ACCEPT}
                            onSelect={onSelectPrimaryLogo}
                            onRemove={onRemovePrimaryLogo}
                        />
                        {submitAttempted && errors.primaryLogo && (
                            <Text fontSize="1.3" topMargin="0.6" color="#D92D20" style={fontRegular}>
                                {primaryLogoError || REQUIRED_MSG}
                            </Text>
                        )}
                    </Box>
                </Box>
                <Box>
                    <Text fontSize="1.4" style={{ ...fontMedium, color: '#344054' }}>
                        Favicon Logo
                    </Text>
                    <Box topMargin="0.8">
                        <FileUpload
                            className="institution-upload"
                            value={favicon}
                            hint={LOGO_HINT}
                            accept={LOGO_ACCEPT}
                            onSelect={onSelectFavicon}
                            onRemove={onRemoveFavicon}
                        />
                        {submitAttempted && errors.favicon && (
                            <Text fontSize="1.3" topMargin="0.6" color="#D92D20" style={fontRegular}>
                                {faviconError || REQUIRED_MSG}
                            </Text>
                        )}
                    </Box>
                </Box>
            </div>

            {/* Advanced/operational settings — test account credentials, loan
                policy, Recova integration code. Only meaningful once the
                institution is fully set up, so only shown in configuration
                mode rather than during the onboarding wizard. */}
            {isComplete && (
                <>
                    <Text topMargin="3.2" fontSize="1.8" style={fontMedium} className="institution-subsection">
                        Advanced Settings
                    </Text>

                    <div className="form-row">
                        <TextField
                            label="Test Account Username" placeholder="Enter test account username"
                            helperText="Optional — used for sandbox/test transactions"
                            value={form.testAccountUsername} onChange={set('testAccountUsername')}
                        />
                        <TextField
                            label="Test Account Number" placeholder="Enter test account number"
                            value={form.testAccountNumber} onChange={set('testAccountNumber')}
                        />
                    </div>

                    <div className="form-row">
                        <TextField
                            label="Test Account Customer ID" placeholder="Enter test account customer ID"
                            value={form.testAccountCustomerId} onChange={set('testAccountCustomerId')}
                        />
                        <TextField
                            label="Recova Institution Code" placeholder="Enter Recova institution code"
                            value={form.recovaInstitutionCode} onChange={set('recovaInstitutionCode')}
                        />
                    </div>

                    <div className="form-row full-width">
                        <TextField
                            label="Loan Policy URL" prefix="http://"
                            value={form.loanPolicyUrl} onChange={set('loanPolicyUrl')}
                        />
                    </div>

                    <div className="form-row">
                        <TextField
                            label="Android App URL" placeholder="Enter Google Play Store URL"
                            helperText="Shown as a download badge on the login page"
                            value={form.androidAppUrl} onChange={set('androidAppUrl')}
                        />
                        <TextField
                            label="iOS App URL" placeholder="Enter Apple App Store URL"
                            helperText="Shown as a download badge on the login page"
                            value={form.iosAppUrl} onChange={set('iosAppUrl')}
                        />
                    </div>
                </>
            )}

            <Flex direction="column" items="flex-end" topMargin="3.2">
                <Button
                    variant="primary"
                    size="md"
                    disabled={!isFormValid || isSubmitting}
                    onClick={handleSubmit}
                >
                    {isSubmitting ? 'Submitting...' : isComplete ? 'Save Changes' : 'Submit'}
                </Button>
                {submitAttempted && !isFormValid && (
                    <Text fontSize="1.3" topMargin="0.8" color="#D92D20" style={fontRegular}>
                        Please check the highlighted fields, something's missing.
                    </Text>
                )}
            </Flex>
        </Card>
    )

    // ---- Setup complete: configuration shell (sidebar + tabbed header) ----
    if (isComplete) {
        return (
            <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
                <Flex direction="column" gap="0.6" items="flex-start">
                    <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                        Institution Profile
                    </Text>
                    <Text fontSize="1.4" color={tokens.textMuted} style={fontRegular}>
                        Manage your institution's basic information and regulatory details
                    </Text>
                </Flex>

                {formCard}
            </div>
        )
    }

    // ---- Still onboarding: original wizard shell ----
    return (
        <Box bgColor={tokens.pageBg} minHeight="100vh">
            <Header sticky initials="QO" />

            <div className="institution-profile-page" style={{ padding: `3.2rem ${PAGE_PADDING_X}` }}>
                <BackLink onClick={() => navigate(-1)}>
                    Go back
                </BackLink>

                <Flex justify="space-between" items="flex-start" topMargin="1.6">
                    <Box className="institution-header">
                        <Text fontSize="2.4" style={fontBold}>
                            Complete Institution Profile
                        </Text>
                        <Text topMargin="0.6" fontSize="1.4" color={tokens.textMuted} style={fontRegular}>
                            Establish the institution's identity and communication channels.
                        </Text>
                    </Box>
                    {!profileCompleted && (
                        <CircularProgress value={progress} caption="Step 1 of 4" />
                    )}
                </Flex>

                {formCard}
            </div>
        </Box>
    )
}

export default utility.routeData('institution-profile', 'Complete Institution Profile', <InstitutionProfile />)